import { v4 } from 'uuid';
import type { RouteLocationNormalized } from 'vue-router';
import { useGlobalStore } from '~/stores/global';
import { useAnalyticsStore } from '~/stores/analytics';
import { maxAge, pagesWithoutSeoData, sessionMaxAge } from '~/common/const';
import { API } from '@/api/api';
import { useInteractiveStore } from '~/stores/interactive';

export default defineNuxtPlugin(() => {
  const route = useRoute();
  const router = useRouter();
  const globalStore = useGlobalStore();
  const analyticsStore = useAnalyticsStore();
  const interactiveStore = useInteractiveStore();
  if (interactiveStore.isPageSpeedBot) return;
  // сделать что б яндекс метрика не работало при прямых переходах (type-in) из линукса
  const runtimeConfig = useRuntimeConfig();
  const isProd = runtimeConfig.public.isProd;
  if (
    (import.meta.env.VITE_ENABLE_STATISTICS &&
      import.meta.env.VITE_ENABLE_STATISTICS.includes('true')) ||
    (isProd && !globalStore.webview)
  ) {
    const domain = window.location.hostname.split('.').slice(-2).join('.');
    const cookieLastAction = useCookie('sessionLastAction', {
      path: '/',
      domain,
      maxAge,
    });
    const cookieSession = useCookie('session', {
      path: '/',
      domain,
      maxAge,
    });
    const cookieOtherSessionData = useCookie('otherSessionData', {
      path: '/',
      domain,
      maxAge,
    });
    const isSessionExpired = () => {
      return (
        Date.now() - (cookieLastAction.value ? +cookieLastAction.value : 0) >
        sessionMaxAge * 1000
      );
    };

    const isVisitFromOtherDomain = () => {
      if (document.referrer) {
        let urlParts;
        try {
          urlParts = new URL(document.referrer).hostname.split('.');
        } catch {
          return false;
        }
        const host = window.location.hostname.split('.').slice(-2).join('.');
        return urlParts.slice(-2).join('.') !== host;
      }

      return false;
    };

    const isReload = () => {
      const nav: PerformanceNavigationTiming =
        window.performance?.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming;
      return nav?.type === 'reload';
    };

    const newSession = (
      query: any,
      isReferer = false,
      currentSession: any = null
    ) => {
      const noNewSession = isReferer && currentSession && isReload();
      const session = noNewSession
        ? currentSession
        : {
            id: v4(),
            depth: 0,
            ts: new Date().toISOString(),
            rhost: document.referrer || '',
            cpc: !!globalStore.sem,
          };
      analyticsStore.setSession(session);
      cookieSession.value = JSON.stringify(session);
      const split = query._split || analyticsStore.additional.split || 'a';
      const utm_medium =
        query.utm_medium || analyticsStore?.additional?.utm_medium;
      const utm_source =
        query.utm_source || analyticsStore?.additional?.utm_source;
      const utm_campaign =
        query.utm_campaign || analyticsStore?.additional?.utm_campaign;
      const utm_content =
        query.utm_content || analyticsStore?.additional?.utm_content;
      const utm_term = query.utm_term || analyticsStore?.additional?.utm_term;
      analyticsStore.setAdditional({
        split,
        utm_medium,
        utm_source,
        utm_campaign,
        utm_content,
        utm_term,
      });
      cookieOtherSessionData.value = JSON.stringify(analyticsStore.additional);
    };

    const setDebugSessionData = (
      key: string,
      referer: string | null = null
    ) => {
      if (window) {
        window.debugSessionData = {
          ...window.debugSessionData,
          [`${key}_${v4()}`]: {
            isCookieSession: !cookieSession.value,
            isSessionExpired: isSessionExpired(),
            referer,
            isVisitFromOtherDomain: isVisitFromOtherDomain(),
            LastAction: cookieLastAction.value,
            cookieSession: cookieSession.value,
          },
        };
      }
    };

    const checkSession = (query: any, referer: string | null) => {
      const session =
        typeof cookieSession.value === 'string'
          ? JSON.parse(cookieSession.value)
          : cookieSession.value;
      const otherSession =
        typeof cookieOtherSessionData.value === 'string'
          ? JSON.parse(cookieOtherSessionData.value)
          : cookieOtherSessionData.value;
      if (
        !cookieSession.value || // do not have a session cookie
        isSessionExpired() || // last action more that threshold
        (!referer && isVisitFromOtherDomain()) // check referer for initial page loading only
      ) {
        const isReferer = !referer && isVisitFromOtherDomain();
        setDebugSessionData('newSession_from_check_before', referer);
        newSession(query, isReferer, session);
        setDebugSessionData('newSession_from_check_after', referer);
      } else {
        analyticsStore.setSession(session);
        let otherSessionData = null;
        if (cookieOtherSessionData.value) {
          otherSessionData =
            typeof otherSession === 'string'
              ? JSON.parse(otherSession)
              : otherSession;
        }
        if (otherSessionData) {
          const split = query._split || otherSessionData.split || 'a';
          analyticsStore.setAdditional({
            ...otherSessionData,
            split,
          });
        }
      }
    };

    /**
     * @param route - current route
     * @param {string|null} referer - from.fullPath or null
     */
    const pageviewEvent = async (
      route: RouteLocationNormalized,
      referer: string | null
    ) => {
      const cookieSem = useCookie('sem', {
        path: '/',
        domain,
        maxAge,
      });
      if (Object.keys(route.query).length > 0 && analyticsStore.isNewSession) {
        setDebugSessionData('newSession_pageviewEvent_before');
        newSession(route.query);
        setDebugSessionData('newSession_pageviewEvent_after');
      } else {
        checkSession(route.query, referer);
      }
      analyticsStore.setPageviewId(v4());
      analyticsStore.setDepth();

      cookieSession.value = JSON.stringify(analyticsStore.session);
      cookieLastAction.value = Date.now().toString();
      const isSem = route?.query?.utm_medium === 'cpc';
      if (!cookieSem.value && globalStore.sem && referer && !isSem) {
        globalStore.setSem(0);
      }
      const thisRoute = route.path;
      const replaceUrl = () => {
        return thisRoute.endsWith('/')
          ? thisRoute.slice(1, -1).replace(/\//g, '-')
          : thisRoute.replace(/\//g, '');
      };
      const page =
        (pagesWithoutSeoData.find((pageName) => {
          const name = route.name as string;
          if (!name) return false;
          return name.search(pageName) !== -1;
        }) &&
          replaceUrl()) ||
        globalStore.seoData?.page?.type ||
        'unknown';

      const context = globalStore.seoData?.page?.context || {};
      const sendReferrer = referer
        ? location.origin + referer
        : route?.query?.utm_referrer || document.referrer;

      const body = {
        user_id: analyticsStore.userId,
        session_id: analyticsStore.session.id,
        pageview_id: analyticsStore.pageviewId,
        user_agent: navigator.userAgent,
        url: window.location.href,
        page,
        depth: analyticsStore.session.depth,
        region_id: globalStore.region.id,
        referer: sendReferrer,
        user_ip: analyticsStore.userIp,
        device: globalStore.device,
        ...analyticsStore.additional,
        ...context,
      };
      const headers = {
        type: 'application/json',
      };
      const blob = new Blob([JSON.stringify(body)], headers);
      if (navigator.sendBeacon)
        navigator.sendBeacon('/v4/stats/pageview', blob);
      else
        await API.post('/stats/pageview', body).catch(() => {
          // console.error('now page view');
        });
    };

    analyticsStore.initAnalytics(true);

    analyticsStore.initViewObserve();

    setInterval(() => {
      if (navigator.sendBeacon)
        navigator.sendBeacon(
          `/v4/stats/ping?session_id=${analyticsStore.session.id}&pageview_id=${analyticsStore.pageviewId}`
        );
      else
        API.post(
          '/stats/ping',
          {},
          {
            params: {
              session_id: analyticsStore.session.id,
              pageview_id: analyticsStore.pageviewId,
            },
          }
        ).catch(() => {});
    }, 15000);

    pageviewEvent(route, null);
    router.afterEach((to, from) => {
      if (from.name && from.fullPath !== to.fullPath)
        pageviewEvent(to, from.fullPath);
    });
  }
});
